import React from "react";
import { Video, Clock, RadioTower, ListFilter, Scale } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";

const MODES = [
    {
        icon: RadioTower,
        title: "Live (real-time)",
        body: "Cameras stream into recognition software continuously. Every passer-by is scanned against a watchlist as they walk — no detention, no questioning, no knowledge. This is the mode most likely to chill lawful behaviour, and the mode NZ Police policy now explicitly bans (Radio New Zealand, 2024).",
        status: "BANNED IN NZ POLICE POLICY (2024)",
        tone: "alert",
    },
    {
        icon: Clock,
        title: "Retrospective",
        body: "Recorded CCTV footage is searched after an incident. The officer uploads a still of an unknown suspect and the software returns ranked candidate matches from existing images — e.g. lawful-access mugshots.",
        status: "PERMITTED WITH APPROVAL (2024 POLICY)",
        tone: "amber",
    },
    {
        icon: Video,
        title: "Mobile / body-worn",
        body: "Recognition runs on portable devices — the mode at issue in Bridges v South Wales Police, where officers scanned crowds with cameras in vans and on chests. Courts found its use unlawful for process failures, not because the software could not work (Bridges v South Wales Police, 2020).",
        status: "REGULATED ABROAD, UNTESTED IN NZ",
        tone: "ash",
    },
];

export default function FrtAndCctv() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-02"
                lines={["Facial recognition", <>+ <i className="text-amber">CCTV</i></>]}
                lede="CCTV is the nervous system of modern public space; facial recognition is what happens when that nervous system learns to recognise. A camera that merely records watches everyone equally. A camera connected to FRT interrogates everyone who passes — turning passive footage into active identification."
                meta={["SUBJECT: THE SENSOR LAYER"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="2.1"
                        kicker="Three modes"
                        title="Same cameras, three very different risks"
                        lede="The distinction matters because each mode changes who is affected and what oversight is needed."
                    />
                    <div className="grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {MODES.map((m, i) => (
                            <Reveal key={m.title} delay={i * 0.08} className="bg-ink">
                                <div className="p-7 sm:p-8 h-full flex flex-col" data-testid={`cctv-mode-card-${i + 1}`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <m.icon size={20} className="text-amber" />
                                        <span
                                            className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border ${
                                                m.tone === "alert"
                                                    ? "text-alert border-alert/40"
                                                    : m.tone === "amber"
                                                    ? "text-amber border-amber/40"
                                                    : "text-ash border-white/20"
                                            }`}
                                        >
                                            {m.status}
                                        </span>
                                    </div>
                                    <h3 className="font-display text-2xl text-paper mb-4">{m.title}</h3>
                                    <p className="text-sm leading-relaxed text-ash">{m.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div>
                        <SectionHead
                            no="2.2"
                            kicker="Why the camera network matters"
                            title="Coverage decides the question before it is asked"
                        />
                        <div className="space-y-6 text-base leading-relaxed text-ash">
                            <Reveal>
                                <p>
                                    A facial recognition system is only as broad as the camera network feeding it.
                                    Dense city-centre CCTV — transport hubs, retail networks, council cameras — means
                                    a citizen can be scanned dozens of times on one walk home. Each scan is a query
                                    against someone's identity, and none of them require a warrant or the person's
                                    knowledge (Mann &amp; Smith, 2017).
                                </p>
                            </Reveal>
                            <Reveal delay={0.08}>
                                <p>
                                    Image quality is the second bottleneck. Recognition accuracy collapses with
                                    distance, angle and low light — precisely the conditions of street surveillance —
                                    and degraded inputs increase the odds of a false match against the wrong person.
                                </p>
                            </Reveal>
                            <Reveal delay={0.14}>
                                <p>
                                    The third issue is <span className="text-paper">function creep</span>: networks
                                    built for one purpose (traffic management, safety) quietly acquire another
                                    (identification). The New Zealand experience — a proof-of-concept trial in 2018,
                                    an unapproved Clearview AI trial in 2020 — shows how easily that line is crossed
                                    before the public, or even the Privacy Commissioner, is told (Independent Police
                                    Conduct Authority, 2020).
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="space-y-10 lg:pt-4">
                        <FigureBlock
                            num="3"
                            src="https://images.unsplash.com/photo-1599350686877-382a54114d2f?q=80&w=1600&auto=format&fit=crop"
                            alt="Security camera mounted on a wall beside street flowers"
                            caption="Street-level cameras are the physical substrate of any recognition capability. Their placement — not their software — decides which streets, and which people, are watched."
                            ratio="4/3"
                        />
                        <FigureBlock
                            num="4"
                            src="https://images.unsplash.com/photo-1630241772217-5d4926c594cc?q=80&w=1600&auto=format&fit=crop"
                            alt="Cluster of security cameras pointing in different directions"
                            caption="Overlapping fields of view make anonymised movement in public space practically impossible: every direction of travel is covered by at least one lens."
                            ratio="4/3"
                        />
                    </div>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20 grid sm:grid-cols-2 gap-8">
                    <Reveal>
                        <div className="flex gap-4">
                            <ListFilter size={20} className="text-amber shrink-0 mt-1" />
                            <p className="text-sm sm:text-base leading-relaxed text-ash">
                                <span className="text-paper font-medium">The watchlist is the policy.</span> Whether a
                                CCTV+FRT system searches for violent offenders, shoplifters or protest attendees is a
                                choice made by whoever maintains the list. Oversight of the list is therefore more
                                important than oversight of the camera.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="flex gap-4">
                            <Scale size={20} className="text-amber shrink-0 mt-1" />
                            <p className="text-sm sm:text-base leading-relaxed text-ash">
                                <span className="text-paper font-medium">Legal exposure is real.</span> In Bridges v
                                South Wales Police (2020), the Court of Appeal held that a democratic society is
                                entitled to know how such tools are used — and found the force's process unlawful
                                because it never adequately examined bias or safeguards.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
